/**
 * 智能压缩器 - 基于文件大小的动态压缩策略
 * 
 * 核心理念：
 * - 小文件：快速处理，保持质量
 * - 中文件：平衡速度与质量
 * - 大文件：优先压缩率，可接受较长处理时间
 */

import { ParameterCompressor } from './ParameterCompressor';
import { CanvasCompressor } from './CanvasCompressor';
import { FFmpegCompressor } from './FFmpegCompressor';
import { ImageCompressor } from './ImageCompressor';

interface SmartCompressResult {
  success: boolean;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  blob: Blob;
  processingTime: number;
  method: string;
  strategy: string;
  qualityLevel: string;
  error?: string;
}

interface FileCategory {
  name: string;
  minSizeMB: number;
  maxSizeMB: number;
  method: 'parameter' | 'canvas' | 'webcodecs' | 'ffmpeg';
  fallbackMethod: 'parameter' | 'canvas';
  config: any;
  description: string;
  priority: 'speed' | 'balance' | 'quality' | 'compression';
}

/**
 * 基于文件大小的压缩策略配置
 * 📋 策略分级说明：
 * - 微小文件 (0-10MB): 参数压缩，极速处理
 * - 小文件 (10-50MB): 参数压缩，快速处理
 * - 中等文件 (50-150MB): WebCodecs硬件加速
 * - 大文件 (150-500MB): Canvas绘制，重度压缩
 * - 超大文件 (500-1500MB): FFmpeg专业压缩
 * - 巨型文件 (>1500MB): FFmpeg极限压缩
 */
const COMPRESSION_STRATEGIES: FileCategory[] = [
  {
    name: '微小文件',
    minSizeMB: 0,
    maxSizeMB: 10,
    method: 'parameter',
    fallbackMethod: 'parameter',
    config: {
      maxSizeMB: 8,
      videoBitsPerSecond: 2000000, // 2Mbps - 保持较高质量
      audioBitsPerSecond: 128000,  // 128kbps
      // mimeType留空，让ParameterCompressor自动选择最佳格式
    },
    description: '快速处理，保持高质量',
    priority: 'speed'
  },
  {
    name: '小文件',
    minSizeMB: 10,
    maxSizeMB: 50,
    method: 'parameter',
    fallbackMethod: 'parameter', 
    config: {
      maxSizeMB: 25,
      videoBitsPerSecond: 1500000, // 1.5Mbps
      audioBitsPerSecond: 96000,   // 96kbps
      // mimeType留空，让ParameterCompressor自动选择最佳格式
    },
    description: '快速处理，轻度压缩，保持良好质量',
    priority: 'speed'
  },
  {
    name: '中等文件',
    minSizeMB: 50,
    maxSizeMB: 150,
    method: 'webcodecs',
    fallbackMethod: 'canvas',
    config: {
      maxSizeMB: 80,
      width: 1920,
      height: 1080,
      fps: 30,
      quality: 0.8,
      bitrate: 1000000 // 1Mbps
    },
    description: '硬件加速，平衡质量与大小',
    priority: 'balance'
  },
  {
    name: '大文件',
    minSizeMB: 150,
    maxSizeMB: 500,
    method: 'canvas',
    fallbackMethod: 'canvas',
    config: {
      maxSizeMB: 120,
      width: 1280,
      height: 720,
      fps: 24,
      quality: 0.7,
      bitrate: 800000 // 800kbps
    },
    description: '重度压缩，显著减小文件大小',
    priority: 'compression'
  },
  {
    name: '超大文件',
    minSizeMB: 500,
    maxSizeMB: 1500,
    method: 'ffmpeg',
    fallbackMethod: 'canvas',
    config: {
      maxSizeMB: 200,
      width: 1280,
      height: 720,
      fps: 20,
      quality: 0.6,
      bitrate: '600k',
      preset: 'aggressive'
    },
    description: '专业压缩，追求最小文件大小',
    priority: 'compression'
  },
  {
    name: '巨型文件',
    minSizeMB: 1500,
    maxSizeMB: Infinity,
    method: 'ffmpeg',
    fallbackMethod: 'canvas',
    config: {
      maxSizeMB: 300,
      width: 854,
      height: 480,
      fps: 15,
      quality: 0.5,
      bitrate: '400k',
      preset: 'ultra_aggressive'
    },
    description: '极限压缩，牺牲质量换取最小体积',
    priority: 'compression'
  }
];

// 支持的压缩方法枚举
export type CompressorMethod = 'auto' | 'ffmpeg' | 'image' | 'parameter' | 'webcodecs' | 'canvas';

export class SmartCompressor {
  private enableProgressLog: boolean = true;
  private forceMethod: CompressorMethod = 'auto';

  constructor(options: { 
    enableProgressLog?: boolean;
    forceMethod?: CompressorMethod;
  } = {}) {
    this.enableProgressLog = options.enableProgressLog ?? true;
    this.forceMethod = options.forceMethod ?? 'auto';
  }

  /**
   * 🎯 智能压缩主入口
   */
  async compress(file: File): Promise<SmartCompressResult> {
    const startTime = Date.now();
    const fileSizeMB = file.size / (1024 * 1024);

    try {
      // 🔧 强制指定压缩方法模式
      if (this.forceMethod !== 'auto') {
        const methodName = this.getMethodDisplayName(this.forceMethod);
        if (this.enableProgressLog) {
          console.log(`\n🔧 === 强制${methodName}压缩模式 ===`);
          console.log(`📁 文件: ${file.name}`);
          console.log(`📊 大小: ${fileSizeMB.toFixed(2)}MB`);
          console.log(`🎨 类型: ${file.type}`);
          console.log(`⚙️ 方法: ${methodName} (强制模式)`);
          console.log(`================================\n`);
        }

        return await this.forceCompressWithMethod(file, this.forceMethod, fileSizeMB, startTime);
      }

      // 🖼️ 图片文件强制使用图片压缩器
      if (file.type.startsWith('image/')) {
        if (this.enableProgressLog) {
          console.log(`\n🖼️ === 图片专用压缩器 ===`);
          console.log(`📁 文件: ${file.name}`);
          console.log(`📊 大小: ${fileSizeMB.toFixed(2)}MB`);
          console.log(`🎨 类型: ${file.type}`);
          console.log(`⚙️ 方法: ImageCompressor (图片专用)`);
          console.log(`💡 策略: 图片文件强制使用专用压缩器`);
          console.log(`================================\n`);
        }

        const result = await ImageCompressor.smartCompress(file);
        
        return {
          success: result.success,
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
          compressionRatio: result.compressionRatio,
          blob: result.blob,
          processingTime: Date.now() - startTime,
          method: 'image',
          strategy: '图片专用压缩器',
          qualityLevel: '优化质量 (保持可读性)',
          error: result.error
        };
      }

      // 🎬 视频文件处理（原有逻辑）
      const strategy = this.selectStrategy(fileSizeMB);
      
      if (this.enableProgressLog) {
        console.log(`\n🎯 === 智能压缩策略分析 ===`);
        console.log(`📁 文件: ${file.name}`);
        console.log(`📊 大小: ${fileSizeMB.toFixed(2)}MB`);
        console.log(`📋 策略: ${strategy.name}`);
        console.log(`📝 描述: ${strategy.description}`);
        console.log(`⚙️ 方法: ${strategy.method} (备用: ${strategy.fallbackMethod})`);
        console.log(`🎯 优先级: ${strategy.priority}`);
        console.log(`🔧 配置:`, strategy.config);
        console.log(`================================\n`);
      }

      // 🔍 如果选择的是parameter方法，进行多层预检查
      if (strategy.method === 'parameter') {
        const multiLayerResult = await this.multiLayerParameterPreCheck(file, fileSizeMB);
        if (!multiLayerResult.shouldCompress) {
          // 不符合压缩条件，返回原文件
                     return {
             success: true,
             originalSize: file.size,
             compressedSize: file.size,
             compressionRatio: 1,
             blob: new Blob([file], { type: file.type }),
             processingTime: Date.now() - startTime,
             method: 'parameter',
             strategy: multiLayerResult.strategy || '智能模式 (跳过压缩)',
             qualityLevel: multiLayerResult.reason,
             error: multiLayerResult.decisionPath.join(' → ')
           };
        } else if (multiLayerResult.compressionConfig) {
          // 使用多层预检查的动态配置更新strategy
          strategy.config = {
            quality: multiLayerResult.compressionConfig.quality,
            targetSizeMB: (multiLayerResult.compressionConfig.targetBitrate * fileSizeMB * 8 * 1024 * 1024) / (file.size * 8) // 根据比特率调整
          };
        }
      }

      // 🚀 执行压缩
      const result = await this.executeCompression(file, strategy);
      
      // 📊 计算最终结果
      const finalResult: SmartCompressResult = {
        ...result,
        processingTime: Date.now() - startTime,
        strategy: strategy.name,
        qualityLevel: this.getQualityLevel(strategy)
      };

      // 📈 输出压缩报告
      if (this.enableProgressLog) {
        this.logCompressionReport(finalResult, fileSizeMB);
      }

      return finalResult;

    } catch (error) {
      console.error('💥 压缩过程发生错误:', error);
      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime: Date.now() - startTime,
        method: 'error',
        strategy: '错误处理',
        qualityLevel: '未知',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * 📊 根据文件大小选择最优策略
   */
  private selectStrategy(fileSizeMB: number): FileCategory {
    for (const strategy of COMPRESSION_STRATEGIES) {
      if (fileSizeMB >= strategy.minSizeMB && fileSizeMB < strategy.maxSizeMB) {
        return strategy;
      }
    }
    
    // 默认返回最后一个策略（巨型文件策略）
    return COMPRESSION_STRATEGIES[COMPRESSION_STRATEGIES.length - 1];
  }

  /**
   * ⚙️ 执行具体的压缩操作
   */
  private async executeCompression(file: File, strategy: FileCategory): Promise<Omit<SmartCompressResult, 'processingTime' | 'strategy' | 'qualityLevel'>> {
    try {
      // 尝试主要方法
      if (this.enableProgressLog) {
        console.log(`🚀 尝试主要压缩方法: ${strategy.method}`);
      }
      return await this.compressWithMethod(file, strategy.method, strategy.config);
    } catch (error) {
      console.warn(`⚠️ 主要方法 ${strategy.method} 失败，尝试备用方法 ${strategy.fallbackMethod}:`, error);
      
      // 尝试备用方法
      try {
        const fallbackConfig = this.getFallbackConfig(strategy);
        if (this.enableProgressLog) {
          console.log(`🔄 切换到备用方法: ${strategy.fallbackMethod}`);
        }
        return await this.compressWithMethod(file, strategy.fallbackMethod, fallbackConfig);
      } catch (fallbackError) {
        console.error(`❌ 备用方法 ${strategy.fallbackMethod} 也失败:`, fallbackError);
        throw fallbackError;
      }
    }
  }

  /**
   * 🔧 使用指定方法进行压缩
   */
  private async compressWithMethod(
    file: File, 
    method: 'parameter' | 'canvas' | 'webcodecs' | 'ffmpeg',
    config: any
  ): Promise<Omit<SmartCompressResult, 'processingTime' | 'strategy' | 'qualityLevel'>> {
    
    switch (method) {
      case 'parameter': {
        const result = await ParameterCompressor.compress(file, config);
        return {
          success: result.success,
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
          compressionRatio: result.compressionRatio,
          blob: result.blob,
          method: 'parameter',
          error: result.error
        };
      }

      case 'canvas': {
        const result = await CanvasCompressor.compress(file, config);
        return {
          success: result.success,
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
          compressionRatio: result.compressionRatio,
          blob: result.blob,
          method: 'canvas',
          error: result.error
        };
      }

      case 'ffmpeg': {
        const result = await FFmpegCompressor.compress(file, config);
        return {
          success: result.success,
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
          compressionRatio: result.compressionRatio,
          blob: result.blob,
          method: 'ffmpeg',
          error: result.error
        };
      }

      default:
        throw new Error(`不支持的压缩方法: ${method}`);
    }
  }

  /**
   * 🔄 获取备用方法的配置
   */
  private getFallbackConfig(strategy: FileCategory): any {
    if (strategy.fallbackMethod === 'parameter') {
      return {
        maxSizeMB: strategy.config.maxSizeMB || 50,
        videoBitsPerSecond: 1000000, // 1Mbps 保守设置
        audioBitsPerSecond: 96000
        // mimeType留空，让ParameterCompressor自动选择最佳格式
      };
    } else {
      return {
        maxSizeMB: strategy.config.maxSizeMB || 100,
        width: 1280,
        height: 720,
        fps: 24,
        quality: 0.7
      };
    }
  }

  /**
   * 🏷️ 获取质量等级描述
   */
  private getQualityLevel(strategy: FileCategory): string {
    switch (strategy.priority) {
      case 'speed': return '高质量 (优先速度)';
      case 'balance': return '良好质量 (平衡模式)';
      case 'quality': return '优秀质量 (优先质量)';
      case 'compression': return '可接受质量 (优先压缩)';
      default: return '未知';
    }
  }

  /**
   * 📈 输出详细的压缩报告
   */
  private logCompressionReport(result: SmartCompressResult, originalSizeMB: number): void {
    const compressedSizeMB = result.compressedSize / (1024 * 1024);
    const compressionPercent = ((1 - result.compressionRatio) * 100);
    const speedMBps = originalSizeMB / (result.processingTime / 1000);
    const savedMB = originalSizeMB - compressedSizeMB;

    console.log(`\n📊 === 智能压缩完成报告 ===`);
    console.log(`✨ 策略: ${result.strategy}`);
    console.log(`🔧 方法: ${result.method}`);
    console.log(`🏷️ 质量: ${result.qualityLevel}`);
    console.log(`📏 原始: ${originalSizeMB.toFixed(2)}MB`);
    console.log(`📦 压缩后: ${compressedSizeMB.toFixed(2)}MB`);
    console.log(`💾 节省: ${savedMB.toFixed(2)}MB (${compressionPercent.toFixed(1)}%)`);
    console.log(`⏱️ 耗时: ${(result.processingTime / 1000).toFixed(2)}秒`);
    console.log(`🚀 速度: ${speedMBps.toFixed(2)}MB/s`);
    
    // 🎯 性能评级
    let performanceGrade = '';
    if (speedMBps > 10) performanceGrade = '🚀 极速';
    else if (speedMBps > 5) performanceGrade = '⚡ 快速';
    else if (speedMBps > 2) performanceGrade = '🚶 一般';
    else performanceGrade = '🐌 较慢';
    
    console.log(`📈 性能: ${performanceGrade}`);
    
    // 🎖️ 压缩效果评级
    let compressionGrade = '';
    if (compressionPercent > 70) compressionGrade = '🏆 优秀';
    else if (compressionPercent > 50) compressionGrade = '🥈 良好';
    else if (compressionPercent > 30) compressionGrade = '🥉 一般';
    else if (compressionPercent > 10) compressionGrade = '📉 轻微';
    else compressionGrade = '📊 保持原样';
    
    console.log(`🎖️ 压缩: ${compressionGrade}`);
    
    // 🎯 显示多层预检查决策路径（如果是Parameter方法）
    if (result.method === 'parameter' && result.error && result.error.includes('Level')) {
      console.log(`🧠 智能决策路径:`);
      result.error.split(' → ').forEach((step, index) => {
        console.log(`   ${index + 1}. ${step}`);
      });
    }
    
    console.log(`===============================\n`);
  }

  /**
   * 📚 批量智能压缩
   */
  async batchCompress(files: File[]): Promise<SmartCompressResult[]> {
    console.log(`🚀 开始批量智能压缩 ${files.length} 个文件\n`);
    
    const results: SmartCompressResult[] = [];
    const startTime = Date.now();
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`⏳ 处理第 ${i + 1}/${files.length} 个文件: ${file.name}`);
      
      try {
        // 临时关闭详细日志
        const originalLogSetting = this.enableProgressLog;
        this.enableProgressLog = false;
        
        const result = await this.compress(file);
        results.push(result);
        
        // 恢复日志设置
        this.enableProgressLog = originalLogSetting;
        
        const status = result.success ? '✅' : '❌';
        const sizeMB = (result.compressedSize / 1024 / 1024).toFixed(1);
        const ratio = ((1 - result.compressionRatio) * 100).toFixed(0);
        console.log(`${status} 完成: ${sizeMB}MB (压缩${ratio}%) - ${result.method}`);
        
      } catch (error) {
        console.error(`❌ 第 ${i + 1} 个文件处理失败:`, error);
        results.push({
          success: false,
          originalSize: file.size,
          compressedSize: file.size,
          compressionRatio: 1,
          blob: new Blob([file], { type: file.type }),
          processingTime: 0,
          method: 'error',
          strategy: '处理失败',
          qualityLevel: '未知',
          error: error instanceof Error ? error.message : '未知错误'
        });
      }
    }
    
    // 📊 批量处理统计
    const totalTime = Date.now() - startTime;
    const successCount = results.filter(r => r.success).length;
    const totalOriginalSize = results.reduce((sum, r) => sum + r.originalSize, 0);
    const totalCompressedSize = results.reduce((sum, r) => sum + r.compressedSize, 0);
    const overallRatio = totalCompressedSize / totalOriginalSize;
    const savedMB = (totalOriginalSize - totalCompressedSize) / 1024 / 1024;
    
    console.log(`\n🎉 === 批量压缩完成统计 ===`);
    console.log(`📁 总文件数: ${files.length} 个`);
    console.log(`✅ 成功: ${successCount} 个`);
    console.log(`❌ 失败: ${files.length - successCount} 个`);
    console.log(`📏 原始总大小: ${(totalOriginalSize / 1024 / 1024).toFixed(2)}MB`);
    console.log(`📦 压缩后总大小: ${(totalCompressedSize / 1024 / 1024).toFixed(2)}MB`);
    console.log(`💾 总节省: ${savedMB.toFixed(2)}MB`);
    console.log(`📉 总压缩率: ${((1 - overallRatio) * 100).toFixed(1)}%`);
    console.log(`⏱️ 总耗时: ${(totalTime / 1000).toFixed(2)}秒`);
    console.log(`🚀 平均速度: ${((totalOriginalSize / 1024 / 1024) / (totalTime / 1000)).toFixed(2)}MB/s`);
    console.log(`===============================\n`);
    
    return results;
  }

  /**
   * 📋 获取策略列表
   */
  getStrategies(): Array<{
    name: string;
    range: string;
    method: string;
    fallback: string;
    description: string;
    priority: string;
  }> {
    return COMPRESSION_STRATEGIES.map(strategy => ({
      name: strategy.name,
      range: `${strategy.minSizeMB}MB - ${strategy.maxSizeMB === Infinity ? '∞' : strategy.maxSizeMB + 'MB'}`,
      method: strategy.method,
      fallback: strategy.fallbackMethod,
      description: strategy.description,
      priority: strategy.priority
    }));
  }

  /**
   * 🔮 预估压缩结果
   */
  async estimateCompression(file: File): Promise<{
    strategy: string;
    method: string;
    estimatedSizeMB: number;
    estimatedRatio: number;
    estimatedTime: number;
    description: string;
  }> {
    const fileSizeMB = file.size / (1024 * 1024);
    const strategy = this.selectStrategy(fileSizeMB);
    
    // 📊 基于历史数据的估算系数
    const estimationFactors = {
      parameter: { ratio: 0.75, timePerMB: 0.1 },  // 参数压缩：快速，压缩率一般
      canvas: { ratio: 0.55, timePerMB: 2.0 },     // Canvas：较慢，压缩率好
      webcodecs: { ratio: 0.45, timePerMB: 0.8 },  // WebCodecs：较快，压缩率很好
      ffmpeg: { ratio: 0.35, timePerMB: 1.5 }      // FFmpeg：中等速度，压缩率优秀
    };
    
    const factor = estimationFactors[strategy.method];
    const estimatedSizeMB = fileSizeMB * factor.ratio;
    const estimatedTime = fileSizeMB * factor.timePerMB * 1000; // 转换为毫秒
    
    return {
      strategy: strategy.name,
      method: strategy.method,
      estimatedSizeMB,
      estimatedRatio: factor.ratio,
      estimatedTime,
      description: strategy.description
    };
  }

  /**
   * 🎛️ 自定义策略压缩
   */
  async compressWithCustomStrategy(
    file: File,
    customStrategy: Partial<FileCategory>
  ): Promise<SmartCompressResult> {
    const fileSizeMB = file.size / (1024 * 1024);
    const defaultStrategy = this.selectStrategy(fileSizeMB);
    
    // 合并自定义策略和默认策略
    const strategy: FileCategory = {
      ...defaultStrategy,
      ...customStrategy,
      config: { ...defaultStrategy.config, ...customStrategy.config }
    };
    
    if (this.enableProgressLog) {
      console.log(`🎛️ 使用自定义策略压缩:`);
      console.log(`📋 策略名称: ${strategy.name}`);
      console.log(`⚙️ 压缩方法: ${strategy.method}`);
      console.log(`🔧 自定义配置:`, strategy.config);
    }
    
    const startTime = Date.now();
    
    try {
      const result = await this.executeCompression(file, strategy);
      
      const finalResult: SmartCompressResult = {
        ...result,
        processingTime: Date.now() - startTime,
        strategy: `自定义: ${strategy.name}`,
        qualityLevel: this.getQualityLevel(strategy)
      };
      
      if (this.enableProgressLog) {
        this.logCompressionReport(finalResult, fileSizeMB);
      }
      
      return finalResult;
      
    } catch (error) {
      console.error('❌ 自定义策略压缩失败:', error);
      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime: Date.now() - startTime,
        method: 'error',
        strategy: '自定义策略失败',
        qualityLevel: '未知',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * 🔧 获取FFmpeg配置 (强制模式)
   */
  private getFFmpegConfig(file: File, fileSizeMB: number): any {
    // 基于文件大小和类型优化FFmpeg参数
    if (file.type.startsWith('image/')) {
      // 图片文件也使用FFmpeg处理 (强制模式特有)
      return {
        format: 'webp',
        quality: fileSizeMB > 10 ? 70 : 80,
        preset: 'photo',
        enableProgressLog: this.enableProgressLog
      };
    }

    // 视频文件配置
    if (fileSizeMB <= 50) {
      // 小文件: 保持质量
      return {
        crf: 23,
        preset: 'fast',
        codec: 'libx264',
        audioCodec: 'aac',
        format: 'mp4',
        enableProgressLog: this.enableProgressLog
      };
    } else if (fileSizeMB <= 200) {
      // 中等文件: 平衡质量与大小
      return {
        crf: 26,
        preset: 'medium',
        resolution: '1920x1080',
        bitrate: '2000k',
        codec: 'libx264',
        audioCodec: 'aac',
        format: 'mp4',
        enableProgressLog: this.enableProgressLog
      };
    } else if (fileSizeMB <= 500) {
      // 大文件: 优先压缩
      return {
        crf: 28,
        preset: 'slow',
        resolution: '1280x720',
        bitrate: '1500k',
        fps: 30,
        codec: 'libx264',
        audioCodec: 'aac',
        format: 'mp4',
        enableProgressLog: this.enableProgressLog
      };
    } else {
      // 超大文件: 极限压缩
      return {
        crf: 32,
        preset: 'veryslow',
        resolution: '854x480',
        bitrate: '800k',
        fps: 24,
        codec: 'libx264',
        audioCodec: 'aac',
        format: 'mp4',
        enableProgressLog: this.enableProgressLog
      };
    }
  }

  /**
   * 🏷️ 获取FFmpeg质量等级描述
   */
  private getFFmpegQualityLevel(fileSizeMB: number): string {
    if (fileSizeMB <= 50) {
      return 'FFmpeg高质量 (快速压缩)';
    } else if (fileSizeMB <= 200) {
      return 'FFmpeg良好质量 (平衡模式)';
    } else if (fileSizeMB <= 500) {
      return 'FFmpeg可接受质量 (优先压缩)';
    } else {
      return 'FFmpeg压缩优先 (极限模式)';
    }
  }

  /**
   * 🔧 设置强制压缩方法
   */
  setForceMethod(method: CompressorMethod): void {
    this.forceMethod = method;
    if (this.enableProgressLog) {
      const displayName = this.getMethodDisplayName(method);
      console.log(`🔧 SmartCompressor强制压缩方法: ${displayName}`);
    }
  }

  /**
   * 🔧 设置强制FFmpeg模式 (向后兼容)
   */
  setForceFFmpeg(force: boolean): void {
    this.forceMethod = force ? 'ffmpeg' : 'auto';
    if (this.enableProgressLog) {
      console.log(`🔧 SmartCompressor强制FFmpeg模式: ${force ? '启用' : '禁用'}`);
    }
  }

  /**
   * 📋 获取当前配置信息
   */
  getConfiguration(): {
    enableProgressLog: boolean;
    forceMethod: CompressorMethod;
    mode: string;
  } {
    return {
      enableProgressLog: this.enableProgressLog,
      forceMethod: this.forceMethod,
      mode: this.forceMethod === 'auto' ? 'smart' : `force-${this.forceMethod}`
    };
  }

  /**
   * 📛 获取压缩方法的显示名称
   */
  private getMethodDisplayName(method: CompressorMethod): string {
    const methodNames = {
      'auto': '智能选择',
      'ffmpeg': 'FFmpeg',
      'image': '图片压缩器',
      'parameter': '参数压缩器',
      'webcodecs': 'WebCodecs',
      'canvas': 'Canvas画布'
    };
    return methodNames[method] || method;
  }

  /**
   * 🎯 多层智能预检查 - 集成版本
   */
  private async multiLayerParameterPreCheck(file: File, fileSizeMB: number): Promise<{
    shouldCompress: boolean;
    reason: string;
    strategy?: string;
    compressionConfig?: {
      targetBitrate: number;
      quality: number;
    };
    processingTime: number;
    decisionPath: string[];
  }> {
    const startTime = Date.now();
    const decisionPath: string[] = [];

    if (this.enableProgressLog) {
      console.log(`\n🔍 === 多层Parameter预检查开始 ===`);
      console.log(`📁 文件: ${file.name}`);
      console.log(`📊 大小: ${fileSizeMB.toFixed(2)}MB`);
    }

    try {
      // Level 1: 快速预检查
      const quickResult = await this.quickPreCheck(file, fileSizeMB);
      decisionPath.push(`Level 1: ${quickResult.reason}`);

      if (!quickResult.shouldProceed) {
        const processingTime = Date.now() - startTime;
        if (this.enableProgressLog) {
          console.log(`⚡ Level 1 快速检查失败: ${quickResult.reason}`);
          if (quickResult.reason.includes('预估压缩后')) {
            console.log(`📊 说明: 这是基于预估压缩后大小的判断，不是原始文件大小`);
          } else if (quickResult.reason.includes('原始文件大小')) {
            console.log(`📊 说明: 这是基于原始文件大小的判断`);
          } else if (quickResult.reason.includes('时长')) {
            console.log(`📊 说明: 这是基于视频时长的判断`);
          }
          console.log(`⏱️ 处理时间: ${processingTime}ms`);
          console.log(`================================\n`);
        }

        return {
          shouldCompress: false,
          reason: quickResult.reason,
          processingTime,
          decisionPath
        };
      }

      if (this.enableProgressLog) {
        console.log(`✅ Level 1 快速检查通过，进入质量分析...`);
      }

      // Level 2: 视频质量分析
      const qualityInfo = await this.analyzeVideoQuality(file);
      decisionPath.push(`Level 2: ${qualityInfo.qualityLevel}质量，压缩潜力${(qualityInfo.compressionPotential * 100).toFixed(1)}%`);

      if (this.enableProgressLog) {
        console.log(`📋 Level 2 质量分析结果:`, {
          分辨率: `${qualityInfo.resolution.width}x${qualityInfo.resolution.height}`,
          比特率: `${qualityInfo.bitrate.toFixed(0)}kbps`,
          质量等级: qualityInfo.qualityLevel,
          压缩潜力: `${(qualityInfo.compressionPotential * 100).toFixed(1)}%`
        });
      }

      // Level 3: 综合决策
      const decision = this.makeComprehensiveDecision(quickResult, qualityInfo, { sizeMB: fileSizeMB, duration: qualityInfo.duration });
      decisionPath.push(`Level 3: ${decision.reason}`);

      const processingTime = Date.now() - startTime;

      if (this.enableProgressLog) {
        console.log(`🎯 Level 3 综合决策: ${decision.strategy || '跳过压缩'}`);
        console.log(`📝 决策理由: ${decision.reason}`);
        if (decision.shouldCompress && decision.compressionConfig) {
          console.log(`⚙️ 压缩配置:`, {
            目标比特率: `${decision.compressionConfig.targetBitrate}kbps`,
            质量参数: decision.compressionConfig.quality
          });
        }
        console.log(`⏱️ 总处理时间: ${processingTime}ms`);
        console.log(`================================\n`);
      }

      return {
        shouldCompress: decision.shouldCompress,
        reason: decision.reason,
        strategy: decision.strategy,
        compressionConfig: decision.compressionConfig,
        processingTime,
        decisionPath
      };

    } catch (error) {
      const processingTime = Date.now() - startTime;
      console.error('❌ 多层预检查失败:', error);
      decisionPath.push(`错误: ${error instanceof Error ? error.message : '未知错误'}`);

      return {
        shouldCompress: true,
        reason: '预检查失败，允许压缩尝试',
        processingTime,
        decisionPath
      };
    }
  }

  /**
   * ⚡ Level 1: 快速预检查
   */
  private async quickPreCheck(file: File, fileSizeMB: number): Promise<{
    shouldProceed: boolean;
    reason: string;
  }> {
    // 1. 文件大小检查
    if (fileSizeMB < 30) {
      return {
        shouldProceed: false,
        reason: `原始文件大小${fileSizeMB.toFixed(2)}MB < 30MB，快速跳过`
      };
    }

    // 2. 获取视频时长进行检查
    const duration = await this.getVideoDuration(file);

    if (duration > 120) {
      return {
        shouldProceed: false,
        reason: `视频时长${Math.floor(duration/60)}分${Math.floor(duration%60)}秒，超过2分钟限制`
      };
    }

    // 3. 智能预估压缩后大小检查（跟着质量策略走）
    const estimatedSizeMB = await this.estimateCompressedSizeByQuality(file, duration);
    if (estimatedSizeMB > 50) {
      return {
        shouldProceed: false,
        reason: `预估压缩后大小${estimatedSizeMB.toFixed(1)}MB > 50MB限制，跳过压缩`
      };
    }

    return {
      shouldProceed: true,
      reason: '通过基础检查'
    };
  }

  /**
   * 🔍 Level 2: 视频质量分析
   */
  private async analyzeVideoQuality(file: File): Promise<{
    bitrate: number;
    resolution: { width: number; height: number };
    duration: number;
    qualityLevel: 'ultra' | 'high' | 'medium' | 'low';
    compressionPotential: number;
  }> {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;

    return new Promise((resolve, reject) => {
      video.onloadedmetadata = () => {
        try {
          const actualBitrate = (file.size * 8) / video.duration / 1000; // kbps
          const pixels = video.videoWidth * video.videoHeight;

          const qualityLevel = this.determineQualityLevel(actualBitrate, pixels);
          const compressionPotential = this.calculateCompressionPotential(actualBitrate, pixels);

          const info = {
            bitrate: actualBitrate,
            resolution: {
              width: video.videoWidth,
              height: video.videoHeight
            },
            duration: video.duration,
            qualityLevel,
            compressionPotential
          };

          URL.revokeObjectURL(url);
          resolve(info);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('视频质量分析失败'));
      };

      // 5秒超时
      setTimeout(() => {
        URL.revokeObjectURL(url);
        reject(new Error('视频质量分析超时'));
      }, 5000);
    });
  }

  /**
   * 🎯 Level 3: 综合决策
   */
  private makeComprehensiveDecision(
    quickCheck: { shouldProceed: boolean; reason: string },
    qualityInfo: { qualityLevel: string; bitrate: number; compressionPotential: number; duration: number },
    fileInfo: { sizeMB: number; duration: number }
  ): {
    shouldCompress: boolean;
    strategy?: string;
    reason: string;
    compressionConfig?: { targetBitrate: number; quality: number };
  } {
    const { qualityLevel, bitrate, compressionPotential } = qualityInfo;
    const { sizeMB } = fileInfo;

    // 🎯 策略A：质量优先型 - 质量越高越适合压缩
    
    // Ultra质量：轻度压缩，有足够压缩空间
    if (qualityLevel === 'ultra') {
      return {
        shouldCompress: true,
        strategy: '超高质量轻度压缩',
        reason: `超高质量视频(${bitrate.toFixed(0)}kbps)有足够压缩空间，轻度压缩保持优秀画质`,
        compressionConfig: {
          targetBitrate: Math.max(bitrate * 0.9, 2000), // 仅压缩10%，保持极高质量
          quality: 0.95
        }
      };
    }

    // High质量：标准压缩，适度压缩保质量
    if (qualityLevel === 'high') {
      return {
        shouldCompress: true,
        strategy: '高质量标准压缩',
        reason: `高质量视频适合压缩，保持良好画质的同时减小体积`,
        compressionConfig: {
          targetBitrate: Math.max(bitrate * 0.75, 1500),
          quality: 0.85
        }
      };
    }

    // Medium质量：谨慎处理，压缩效果可能有限
    if (qualityLevel === 'medium') {
      if (compressionPotential < 0.2 || sizeMB < 40) {
        return {
          shouldCompress: false,
          reason: `中等质量视频压缩效果有限(${(compressionPotential*100).toFixed(1)}%潜力)，保护现有质量`
        };
      }

      return {
        shouldCompress: true,
        strategy: '中等质量谨慎压缩',
        reason: `中等质量视频谨慎压缩，避免过度损失`,
        compressionConfig: {
          targetBitrate: Math.max(bitrate * 0.8, 1000), // 更保守的压缩
          quality: 0.75
        }
      };
    }

    // Low质量：跳过压缩，避免雪上加霜
    return {
      shouldCompress: false,
      reason: `低质量视频(${bitrate.toFixed(0)}kbps)再压缩可能影响观看体验，保持原状`
    };
  }

  /**
   * 🔧 辅助方法：获取视频时长
   */
  private async getVideoDuration(file: File): Promise<number> {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;

    return new Promise((resolve, reject) => {
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(video.duration);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('获取视频时长失败'));
      };
    });
  }

  /**
   * 🔧 辅助方法：质量等级判断
   */
  private determineQualityLevel(bitrate: number, pixels: number): 'ultra' | 'high' | 'medium' | 'low' {
    if (pixels <= 640 * 480) {        // 480p
      if (bitrate >= 2000) return 'ultra';
      if (bitrate >= 1200) return 'high';
      if (bitrate >= 600) return 'medium';
      return 'low';
    } else if (pixels <= 1280 * 720) { // 720p
      if (bitrate >= 4000) return 'ultra';
      if (bitrate >= 2500) return 'high';
      if (bitrate >= 1200) return 'medium';
      return 'low';
    } else if (pixels <= 1920 * 1080) { // 1080p
      if (bitrate >= 8000) return 'ultra';
      if (bitrate >= 4000) return 'high';
      if (bitrate >= 2000) return 'medium';
      return 'low';
    } else {                          // 4K+
      if (bitrate >= 25000) return 'ultra';
      if (bitrate >= 12000) return 'high';
      if (bitrate >= 6000) return 'medium';
      return 'low';
    }
  }

  /**
   * 🔧 辅助方法：压缩潜力计算
   */
  private calculateCompressionPotential(bitrate: number, pixels: number): number {
    // 根据分辨率计算理论最优比特率
    let optimalBitrate: number;

    if (pixels <= 640 * 480) {
      optimalBitrate = 800;      // 480p
    } else if (pixels <= 1280 * 720) {
      optimalBitrate = 1500;     // 720p
    } else if (pixels <= 1920 * 1080) {
      optimalBitrate = 3000;     // 1080p
    } else {
      optimalBitrate = 8000;     // 4K+
    }

    // 压缩潜力 = (当前比特率 - 最优比特率) / 当前比特率
    const potential = Math.max(0, (bitrate - optimalBitrate) / bitrate);
    return Math.min(1, potential);
  }

  /**
   * 🎯 根据视频质量智能预估压缩后大小（跟着质量策略走）
   */
  private async estimateCompressedSizeByQuality(file: File, duration: number): Promise<number> {
    try {
      // 快速获取视频信息
      const video = document.createElement('video');
      const url = URL.createObjectURL(file);
      video.src = url;

      return new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          try {
            const originalBitrate = (file.size * 8) / video.duration / 1000; // kbps
            const pixels = video.videoWidth * video.videoHeight;
            const qualityLevel = this.determineQualityLevel(originalBitrate, pixels);
            
            let targetVideoBitrate: number;
            let targetAudioBitrate = 128; // 固定音频比特率
            
            // 根据质量等级确定目标比特率（跟着压缩策略走）
            switch (qualityLevel) {
              case 'ultra':
                targetVideoBitrate = Math.max(originalBitrate * 0.9, 2000); // 仅压缩10%
                break;
              case 'high':
                targetVideoBitrate = Math.max(originalBitrate * 0.75, 1500); // 压缩25%
                break;
              case 'medium':
                targetVideoBitrate = Math.max(originalBitrate * 0.8, 1000); // 保守压缩20%
                break;
              case 'low':
                // 低质量视频不压缩，返回原始大小
                URL.revokeObjectURL(url);
                resolve(file.size / 1024 / 1024);
                return;
            }
            
            // 计算预估大小 (单位: MB)
            const estimatedSizeMB = (targetVideoBitrate + targetAudioBitrate) * duration / 8 / 1024;
            
            // 考虑容器开销和编码效率，增加10%的冗余
            const finalEstimatedMB = estimatedSizeMB * 1.1;
            
            if (this.enableProgressLog) {
              console.log(`🎯 智能预估压缩大小 (质量策略):`, {
                原始大小: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
                原始比特率: `${originalBitrate.toFixed(0)}kbps`,
                质量等级: qualityLevel,
                目标视频比特率: `${targetVideoBitrate.toFixed(0)}kbps`,
                目标音频比特率: `${targetAudioBitrate}kbps`,
                预估最终大小: `${finalEstimatedMB.toFixed(2)}MB`
              });
            }
            
            URL.revokeObjectURL(url);
            resolve(finalEstimatedMB);
          } catch (error) {
            URL.revokeObjectURL(url);
            reject(error);
          }
        };

        video.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('获取视频信息失败'));
        };

        // 3秒超时
        setTimeout(() => {
          URL.revokeObjectURL(url);
          reject(new Error('获取视频信息超时'));
        }, 3000);
      });
    } catch (error) {
      console.error('❌ 智能预估压缩大小失败:', error);
      // 失败时回退到简单预估
      return this.estimateParameterCompressedSize(file, duration);
    }
  }

  /**
   * 📊 预估Parameter压缩后的文件大小（简单版本）
   */
  private estimateParameterCompressedSize(file: File, duration: number): number {
    try {
      // Parameter压缩的基本参数设置
      const targetVideoBitrate = 800; // kbps (较保守的比特率)
      const targetAudioBitrate = 128; // kbps
      
      // 计算预估大小 (单位: MB)
      // 公式: (视频比特率 + 音频比特率) * 时长 / 8 / 1024 / 1024
      const estimatedSizeMB = (targetVideoBitrate + targetAudioBitrate) * duration / 8 / 1024;
      
      // 考虑容器开销和编码效率，增加10%的冗余
      const finalEstimatedMB = estimatedSizeMB * 1.1;
      
      if (this.enableProgressLog) {
        console.log(`🧮 Parameter压缩大小预估(简单版):`, {
          原始大小: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
          视频时长: `${duration.toFixed(1)}秒`,
          目标视频比特率: `${targetVideoBitrate}kbps`,
          目标音频比特率: `${targetAudioBitrate}kbps`,
          理论压缩大小: `${estimatedSizeMB.toFixed(2)}MB`,
          预估最终大小: `${finalEstimatedMB.toFixed(2)}MB`
        });
      }
      
      return finalEstimatedMB;
    } catch (error) {
      console.error('❌ 预估压缩大小失败:', error);
      // 预估失败时返回原文件大小的50%作为保守估计
      return (file.size / 1024 / 1024) * 0.5;
    }
  }

  /**
   * 🔧 强制使用指定方法进行压缩
   */
  private async forceCompressWithMethod(
    file: File, 
    method: CompressorMethod, 
    fileSizeMB: number,
    startTime: number
  ): Promise<SmartCompressResult> {
    try {
      let result: Omit<SmartCompressResult, 'processingTime' | 'strategy' | 'qualityLevel'>;
      let strategyName: string;
      let qualityLevel: string;

      switch (method) {
        case 'ffmpeg': {
          const config = this.getFFmpegConfig(file, fileSizeMB);
          result = await this.compressWithMethod(file, 'ffmpeg', config);
          strategyName = '强制FFmpeg模式';
          qualityLevel = this.getFFmpegQualityLevel(fileSizeMB);
          break;
        }

        case 'image': {
          if (!file.type.startsWith('image/')) {
            throw new Error('图片压缩器仅支持图片文件');
          }
          const imageResult = await ImageCompressor.smartCompress(file);
          result = {
            success: imageResult.success,
            originalSize: imageResult.originalSize,
            compressedSize: imageResult.compressedSize,
            compressionRatio: imageResult.compressionRatio,
            blob: imageResult.blob,
            method: 'image',
            error: imageResult.error
          };
          strategyName = '强制图片压缩模式';
          qualityLevel = '优化质量 (保持可读性)';
          break;
        }

        case 'parameter': {
          // 图片文件仍然使用专用压缩器
          if (file.type.startsWith('image/')) {
            const imageResult = await ImageCompressor.smartCompress(file);
            result = {
              success: imageResult.success,
              originalSize: imageResult.originalSize,
              compressedSize: imageResult.compressedSize,
              compressionRatio: imageResult.compressionRatio,
              blob: imageResult.blob,
              method: 'image',
              error: imageResult.error
            };
            strategyName = '参数模式 (图片→专用压缩器)';
            qualityLevel = '优化质量 (图片专用)';
          } else {
            // 视频文件进行多层智能预检查
            const multiLayerResult = await this.multiLayerParameterPreCheck(file, fileSizeMB);
            
                         if (!multiLayerResult.shouldCompress) {
               // 不符合压缩条件，返回原文件
               result = {
                 success: true,
                 originalSize: file.size,
                 compressedSize: file.size,
                 compressionRatio: 1,
                 blob: new Blob([file], { type: file.type }),
                 method: 'parameter',
                 error: multiLayerResult.decisionPath.join(' → ')
               };
               strategyName = multiLayerResult.strategy || '多层预检查 (跳过压缩)';
               qualityLevel = multiLayerResult.reason;
            } else {
              // 使用动态压缩配置
              const config = multiLayerResult.compressionConfig ? {
                quality: multiLayerResult.compressionConfig.quality,
                targetSizeMB: (multiLayerResult.compressionConfig.targetBitrate * 60) / 8 / 1024 // 假设60秒为基准
              } : {
                quality: 0.7,
                targetSizeMB: Math.min(fileSizeMB * 0.5, 10)
              };
              
              result = await this.compressWithMethod(file, 'parameter', config);
              strategyName = multiLayerResult.strategy || '参数压缩模式';
              qualityLevel = multiLayerResult.reason;
            }
          }
          break;
        }

        case 'canvas': {
          // 图片文件仍然使用专用压缩器
          if (file.type.startsWith('image/')) {
            const imageResult = await ImageCompressor.smartCompress(file);
            result = {
              success: imageResult.success,
              originalSize: imageResult.originalSize,
              compressedSize: imageResult.compressedSize,
              compressionRatio: imageResult.compressionRatio,
              blob: imageResult.blob,
              method: 'image',
              error: imageResult.error
            };
            strategyName = 'Canvas模式 (图片→专用压缩器)';
            qualityLevel = '优化质量 (图片专用)';
          } else {
            const config = { quality: 0.8, targetFormat: 'webp' };
            result = await this.compressWithMethod(file, 'canvas', config);
            strategyName = '强制Canvas模式';
            qualityLevel = '画布渲染压缩';
          }
          break;
        }

        default:
          throw new Error(`不支持的强制压缩方法: ${method}`);
      }

      return {
        ...result,
        processingTime: Date.now() - startTime,
        strategy: strategyName,
        qualityLevel
      };

    } catch (error) {
      console.error(`💥 强制${this.getMethodDisplayName(method)}压缩失败:`, error);
      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime: Date.now() - startTime,
        method: method,
        strategy: `强制${this.getMethodDisplayName(method)}模式 (失败)`,
        qualityLevel: '未知',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }
} 